import { columnStyleOf, type ColumnConfigEntry } from '@erp/shared-types';
import {
  columnAlign,
  flexRender,
  type ColumnDef,
  type VisibilityState,
} from '@/lib/tanstack-table';
import { DataTableColumnHeader } from '@/components/common/data-table/data-table-column-header';
import type { AssignmentLine } from 'src/api/rpc/hooks/procurement/useReplenishmentSuggestions';
import { buyTermColumns } from './buyTermColumns';
import { ColumnStyledCell } from './ColumnStyledCell';
import { cartLineColumns, costColumn, unitCostColumn } from './cartLineColumns';
import { UsageHeaderHint } from './LineUsageCell';
import { usageColumns } from './lineUsageColumns';
import { transferLineColumns } from './transferLineColumns';
import { withCalculatedOnHover } from './withCalculatedOnHover';
import { withNeedRows } from './withNeedRows';
import { PairedQuantityHeader } from './PairedQuantityHeader';
import {
  CALC_COLUMN_ID,
  QUANTITY_COLUMN_ID,
  shownColumns,
  visibilityFromKey,
} from '../utils/buyerBoardColumns';

const BASE_COLUMNS = new Map(
  [
    ...cartLineColumns,
    unitCostColumn,
    costColumn,
    ...buyTermColumns,
    ...usageColumns,
    ...transferLineColumns,
  ].map((column) => [column.id, column]),
);

type LineCell = ColumnDef<AssignmentLine>['cell'];

// One bold renderer per base renderer, so a rebuild of the columns (new lines
// after a save) keeps every cell mounted and an input mid-edit keeps its draft.
const boldCells = new WeakMap<object, LineCell>();

function boldCell(cell: LineCell): LineCell {
  if (typeof cell !== 'function') return cell;
  const cached = boldCells.get(cell);
  if (cached) return cached;
  const bold: LineCell = (context) => (
    <ColumnStyledCell style={{ bold: true }}>
      {flexRender(cell, context)}
    </ColumnStyledCell>
  );
  boldCells.set(cell, bold);
  return bold;
}

/**
 * A line column as the saved view draws it: its header title, its alignment
 * (`meta.align`, which DataTable applies to the header and cells), its cell
 * formatting (bold), and whether a header click sorts. With both drawn, Calc
 * and Qty headers mark the pair.
 */
function configuredColumn(
  base: ColumnDef<AssignmentLine>,
  entry: ColumnConfigEntry,
  pairedSide: 'calc' | 'quantity' | null,
): ColumnDef<AssignmentLine> {
  return {
    ...base,
    id: entry.id,
    meta: { ...base.meta, align: entry.alignRight ? 'right' : 'left' },
    enableSorting: base.enableSorting !== false && entry.sortable !== false,
    cell: columnStyleOf(entry).bold ? boldCell(base.cell) : base.cell,
    header: ({ column }) => {
      const title = (
        <UsageHeaderHint id={entry.id}>
          <DataTableColumnHeader column={column} title={entry.header} />
        </UsageHeaderHint>
      );
      return pairedSide ? (
        <PairedQuantityHeader side={pairedSide}>{title}</PairedQuantityHeader>
      ) : (
        title
      );
    },
  };
}

/** Which half of the Calc/Qty pair a column header draws, while Calc shows. */
export function pairedSideOf(
  id: string,
  calcShown: boolean,
): 'calc' | 'quantity' | null {
  if (!calcShown) return null;
  if (id === CALC_COLUMN_ID) return 'calc';
  return id === QUANTITY_COLUMN_ID ? 'quantity' : null;
}

interface LineTableColumnsInput {
  /** The saved column view, hidden columns flagged. */
  columns: ColumnConfigEntry[];
  lines: readonly Pick<AssignmentLine, 'destinationWarehouseId'>[];
  shipToWarehouseId: string | undefined;
  /** `false` hides a column; `true` keeps it even where this table would drop it. */
  columnVisibility?: VisibilityState;
}

/**
 * Columns for the line table, in the saved view's order with Calc right before
 * Qty. With Calc hidden (the Simple display), Qty names the engine's quantity
 * on hover. On a vendor x ship-to order the order row already names the
 * ship-to, so Warehouse is dropped while every line is destined there; a pivot
 * line bound elsewhere keeps it.
 */
export function lineTableColumns({
  columns,
  lines,
  shipToWarehouseId,
  columnVisibility = {},
}: LineTableColumnsInput): ColumnDef<AssignmentLine>[] {
  const hidden = new Set(
    Object.keys(columnVisibility).filter((id) => !columnVisibility[id]),
  );
  const redundantWarehouse =
    columnVisibility.warehouse !== true &&
    shipToWarehouseId != null &&
    lines.every((l) => l.destinationWarehouseId === shipToWarehouseId);
  if (redundantWarehouse) hidden.add('warehouse');
  const shown = shownColumns(columns, hidden);
  const calcShown = shown.some((entry) => entry.id === CALC_COLUMN_ID);
  return shown.flatMap((entry) => {
    const base = BASE_COLUMNS.get(entry.id);
    if (!base) return [];
    const column =
      entry.id === QUANTITY_COLUMN_ID && !calcShown
        ? withCalculatedOnHover(base)
        : base;
    return [
      configuredColumn(
        withNeedRows(column),
        entry,
        pairedSideOf(entry.id, calcShown),
      ),
    ];
  });
}

/**
 * `lineTableColumns` in a hook of its own, so the compiler caches the columns
 * on exactly these inputs. Visibility arrives as its key (its contents, not
 * the object), so the columns and every header stay put across renders that
 * change no column. Built inline in the board, the columns share a cache scope
 * with the table state, keyed on the whole saved view, and rebuild on a sort.
 */
export function useLineTableColumns({
  columns,
  lines,
  shipToWarehouseId,
  visibilityKey,
}: Omit<LineTableColumnsInput, 'columnVisibility'> & {
  visibilityKey: string;
}): ColumnDef<AssignmentLine>[] {
  // The compiler's infer mode skips a hook that calls no other hook.
  'use memo';
  return lineTableColumns({
    columns,
    lines,
    shipToWarehouseId,
    columnVisibility: visibilityFromKey(visibilityKey),
  });
}

/** Whether a line column's definition marks it numeric (`meta.align`). */
export function isNumericLineColumn(id: string): boolean {
  return columnAlign(BASE_COLUMNS.get(id)?.meta) === 'right';
}
